import React, { useContext } from 'react'
import styled, { css, ThemeContext } from 'styled-components'

// Components
import { Icon } from '../icon'

// Styles
import { fastTransition } from '../../styles/effects'

// Types
import { IconType } from '../icon/IconType'

type SwitchSize = 'small' | 'medium'

interface IProps {
  isOn: boolean
  size?: SwitchSize
  disabled?: boolean
  // When provided the switch renders as an interactive button; otherwise it is a
  // presentational element (e.g. inside an already-clickable row that toggles it).
  onToggle?: () => void
  ariaLabel?: string
  // A mark in the knob for a state that needs saying (locked, hidden). Medium only:
  // the small knob has no room for a 12px icon.
  icon?: IconType
  className?: string
  /** Handle for the e2e suite. */
  'data-cy'?: string
}

// The one on/off toggle switch (track + sliding knob), small or medium. Interactive
// with onToggle, presentational inside a row that already toggles it.
const Switch: React.FC<IProps> = ({
  isOn,
  size = 'medium',
  disabled,
  onToggle,
  ariaLabel,
  icon,
  className,
  'data-cy': dataCy,
}) => {
  const theme = useContext(ThemeContext)
  const knob = (
    <Knob $size={size} $isOn={isOn}>
      {icon && size === 'medium' && (
        <Icon
          type={icon}
          size={12}
          color={isOn ? theme.colors.primary : theme.colors.textLight}
        />
      )}
    </Knob>
  )
  return onToggle ? (
    <TrackButton
      className={className}
      data-cy={dataCy}
      type="button"
      disabled={disabled}
      onClick={onToggle}
      aria-pressed={isOn}
      aria-label={ariaLabel}
      $size={size}
      $isOn={isOn}
      $disabled={disabled}
    >
      {knob}
    </TrackButton>
  ) : (
    <TrackSpan
      className={className}
      data-cy={dataCy}
      aria-hidden
      $size={size}
      $isOn={isOn}
      $disabled={disabled}
    >
      {knob}
    </TrackSpan>
  )
}

export default Switch

// [trackWidth, trackHeight, knobSize] per size, in px. The knob is centred, so
// its inset is computed: (trackHeight - knobSize) / 2.
const DIMENSIONS: Record<SwitchSize, [number, number, number]> = {
  small: [28, 16, 12],
  medium: [48, 28, 24],
}

const inset = (size: SwitchSize) =>
  (DIMENSIONS[size][1] - DIMENSIONS[size][2]) / 2

const trackStyles = css<{
  $size: SwitchSize
  $isOn: boolean
  $disabled?: boolean
}>`
  position: relative;
  display: inline-block;
  flex-shrink: 0;
  border: none;
  padding: 0;
  width: ${({ $size }) => DIMENSIONS[$size][0]}px;
  height: ${({ $size }) => DIMENSIONS[$size][1]}px;
  border-radius: ${({ $size }) => DIMENSIONS[$size][1] / 2}px;
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  background-color: ${({ theme, $isOn }) =>
    $isOn ? theme.colors.primary : theme.colors.textLighter};
  transition: background-color ${fastTransition};

  ${({ $disabled }) =>
    $disabled &&
    css`
      opacity: 0.5;
    `}
`

const TrackButton = styled.button`
  ${trackStyles}
`

const TrackSpan = styled.span`
  ${trackStyles}
`

const Knob = styled.span<{ $size: SwitchSize; $isOn: boolean }>`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  top: ${({ $size }) => inset($size)}px;
  left: ${({ $size, $isOn }) =>
    $isOn
      ? DIMENSIONS[$size][0] - DIMENSIONS[$size][2] - inset($size)
      : inset($size)}px;
  width: ${({ $size }) => DIMENSIONS[$size][2]}px;
  height: ${({ $size }) => DIMENSIONS[$size][2]}px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.lightest};
  transition: left ${fastTransition};
`
